import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle, XCircle, Loader2, Globe } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = async () => {
      try {
        const res = await api.checkPaymentStatus(sessionId);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };
    poll();
  }, [sessionId, attempts]);

  const isDomain = paymentType === 'domain';
  const isEmail = paymentType === 'email';
  const isHosting = paymentType === 'hosting';

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center"><Globe className="w-5 h-5 text-white" /></div>
          <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
        </Link>

        {status === 'checking' && (
          <div className="glass-card p-12">
            <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Verifying Payment</h2>
            <p className="text-slate-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="glass-card p-12">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              {isDomain ? 'Website Purchase Complete!' : isEmail ? 'Email Created!' : isHosting ? 'Hosting Activated!' : 'Payment Successful!'}
            </h2>
            <p className="text-slate-400 mb-6">
              {isDomain
                ? 'Payment received. We are registering your web address now — check your portal for the real status. If registration fails, it will say so there.'
                : isEmail
                ? 'Payment received. Email on EasyPeazy is forwarding, not a full mailbox. Check your portal for the real status.'
                : isHosting
                ? 'Your hosting is being set up now. Make sure your domain\'s DNS points to our server. Check your portal for setup instructions.'
                : 'Your subscription is now active. Start building your website!'}
            </p>
            <Link to="/portal" className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-all" data-testid="go-to-portal-btn">Go to Portal</Link>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div className="glass-card p-12">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Payment Issue</h2>
            <p className="text-slate-400 mb-6">{status === 'expired' ? 'Payment session expired.' : 'Could not verify payment. Check your email for confirmation.'}</p>
            <Link to="/portal" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-lg border border-slate-700 transition-all" data-testid="back-to-portal-btn">Back to Portal</Link>
          </div>
        )}
      </div>
    </div>
  );
}
